import { z } from "zod/v4";
import {
  SAFE_FONT_WEIGHT,
  SAFE_FONT_STYLE,
} from "../../../../brand-profiles/assets/font-validation";
import { isAllowedFontStylesheetUrl } from "../../../../brand-profiles/assets/font-stylesheets";
import { isBrandProfileReviewedUpdatedAtIso } from "../../../../brand-profiles/review";
import { BRAND_FONT_SCOPES } from "../../../../brand-profiles/types";
import {
  AgenticSettingsReviewOpType,
  PROPOSED_ENTITY_KEY_PATTERN,
} from "../../types";

// The frontend imports these schemas; keep database, storage, and Node imports out.
const customFontVariantSchema = z.object({
  id: z.string().optional(),
  url: z.string().min(1),
  weight: z.string().optional(),
  style: z.string().optional(),
});

const customFontVariantProposalSchema = customFontVariantSchema.extend({
  weight: z
    .string()
    .regex(
      SAFE_FONT_WEIGHT,
      "Use a CSS font weight: 100 through 900 in steps of 100, normal, or bold.",
    )
    .optional(),
  style: z
    .string()
    .regex(SAFE_FONT_STYLE, "Use normal, italic, or oblique.")
    .optional(),
});

const fontFamilySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  type: z.enum(["google", "web-safe", "custom"]),
  variants: z.array(z.string()).optional(),
  customVariants: z.array(customFontVariantSchema).optional(),
  stylesheetUrl: z
    .string()
    .refine(isAllowedFontStylesheetUrl, {
      message:
        "stylesheetUrl must be an https URL on fonts.googleapis.com, fonts.bunny.net, or use.typekit.net",
    })
    .optional(),
  scopes: z.array(z.enum(BRAND_FONT_SCOPES)).optional(),
  fallbackFont: z.string().optional(),
  description: z.string().optional(),
  deletedAt: z.string().nullable().optional(),
});

const fontFileReferenceSchema = z.strictObject({
  fileId: z
    .string()
    .min(1)
    .describe(
      "A font file ID from the session uploads or the live profile. The server resolves font metadata.",
    ),
});

// Optional metadata preserves readability of suggestions saved before server resolution.
const storedFontFileSchema = z.object({
  fileId: z.string().min(1),
  fileName: z.string().optional(),
  familyName: z.string().optional(),
  weight: z.string().optional(),
  style: z.string().optional(),
  postscriptName: z.string().optional(),
  preferredFamily: z.string().optional(),
  preferredSubfamily: z.string().optional(),
  variationAxes: z
    .record(
      z.string(),
      z.object({
        name: z.string(),
        min: z.number(),
        default: z.number(),
        max: z.number(),
      }),
    )
    .optional(),
  namedVariations: z
    .record(z.string(), z.record(z.string(), z.number()))
    .optional(),
  contentHash: z.string().optional(),
});

export const brandProfileFontProposalSchema = z.object({
  families: z
    .array(
      fontFamilySchema.extend({
        customVariants: z.array(customFontVariantProposalSchema).optional(),
      }),
    )
    .optional()
    .describe(
      "Full post-edit font families array. Omit to leave families unchanged.",
    ),
  files: z
    .array(fontFileReferenceSchema)
    .optional()
    .describe(
      "Full post-edit list of file IDs. Omit to leave files unchanged. New files must already be uploaded; crawl is not a font-file source. Do not supply metadata.",
    ),
});

const storedFontsSchema = z.object({
  families: z.array(fontFamilySchema).optional(),
  files: z.array(storedFontFileSchema).optional(),
  // Only the server sets this after resolving file references, never the agent.
  metadataVersion: z.literal(1).optional(),
});

// Present arrays replace their column; omitted arrays keep it. Families and files are independent.
const brandProfileStyleColumns = {
  colors: z
    .array(
      z.object({
        hex: z
          .string()
          .min(1)
          .describe(
            "Exactly 6 hex digits, optional leading #. Shorthand (#FFF) and alpha (#FFFFFF80) are invalid.",
          ),
        name: z.string().min(1).optional(),
      }),
    )
    .optional()
    .describe(
      "Full post-edit colors array. Omit to leave colors unchanged; send [] to clear.",
    ),
  logos: z
    .array(
      z.object({
        fileId: z.string().min(1).optional(),
        s3Key: z.string().min(1).optional(),
        imageUrl: z.string().optional(),
        url: z.string().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        altText: z.string().optional(),
      }),
    )
    .optional()
    .describe(
      "Full post-edit logos array. Each entry needs a real image: an upload's fileId, a workspace logo-library s3Key, or, as s3Key, `entity:<entityId>` for a logo image you viewed and saved with save_reference_images this interaction (a logo cropped out of a PDF counts). A prose [Visual: ...] description is not a logo. Omit to leave logos unchanged.",
    ),
};

const contextReviewUpdateBrandProfileOperationSchema = z.object({
  op_type: z.literal(AgenticSettingsReviewOpType.UpdateBrandProfile),
  profile_id: z
    .string()
    .min(1)
    .optional()
    .describe(
      "The id of the existing brand profile that best fits this turn's uploads and brand-profile suggestion, from the context-files index and read_brand_profile. Required when any profile exists; omit only when the index has no brand profiles.",
    ),
  reviewed_updated_at: z
    .string()
    .min(1)
    .refine(isBrandProfileReviewedUpdatedAtIso, {
      message: "reviewed_updated_at must be an ISO-8601 timestamp",
    })
    .optional()
    .describe(
      "The profile updated_at ISO timestamp from read_brand_profile for that same profile. Required when any live profile exists; omit only on the empty-workspace create path.",
    ),
  ...brandProfileStyleColumns,
});

const contextReviewCreateBrandProfileOperationSchema = z.object({
  op_type: z.literal(AgenticSettingsReviewOpType.CreateBrandProfile),
  name: z
    .string()
    .min(1)
    .describe(
      "Display name for the new brand profile, taken from how the supplied material names the brand. Must not match a live profile's name.",
    ),
  // Lifted into its own column and stripped from the persisted jsonb, so it is absent on
  // anything read back from the database.
  proposed_entity_key: z
    .string()
    .regex(
      PROPOSED_ENTITY_KEY_PATTERN,
      "proposed_entity_key must be a slug of lowercase letters, digits and hyphens (max 64 chars), starting with a letter or digit",
    )
    .optional()
    .describe(
      "Stable slug identifying this proposed profile across turns, derived from its name (e.g. 'acme-pro'). Reuse the SAME proposed_entity_key to revise a proposal you already made in this session, INCLUDING when you rename it; use a new one only for a genuinely different profile.",
    ),
  ...brandProfileStyleColumns,
});

export const BRAND_PROFILE_WIRE_OPERATIONS = [
  contextReviewUpdateBrandProfileOperationSchema.extend({
    fonts: brandProfileFontProposalSchema.optional(),
  }),
  contextReviewCreateBrandProfileOperationSchema.extend({
    fonts: brandProfileFontProposalSchema.optional(),
  }),
] as const;

export const BRAND_PROFILE_STORED_OPERATIONS = [
  contextReviewUpdateBrandProfileOperationSchema.extend({
    fonts: storedFontsSchema.optional(),
  }),
  contextReviewCreateBrandProfileOperationSchema.extend({
    fonts: storedFontsSchema.optional(),
  }),
] as const;
